import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import Page, {
  PageMenu,
  PageTitle,
  PageContainer,
  PageContent,
  PageAction,
} from 'src/components/Page';
import Breadcrumbs from 'src/components/Breadcrumbs';
import Button from 'src/components/Button';
import Box from 'src/components/Box';
import UserForm from 'src/components/UserForm';
import Tabs from 'src/components/Tabs';
import SignInLogPage from 'src/pages/SignInLogPage';
import ActivitiesPage from 'src/pages/ActivitiesPage';
import Loader from 'src/components/Loader';
import { Popup } from 'src/components/Popup';
import { EiqButton } from 'eiq-storybook';
import s from 'src/components/UserForm/UserForm.styles';
import { useData, useFormSubmit, usePage, usePasswordReset } from './hooks';

const UserPage = ({ match }) => {
  const { t } = useTranslation();  
  const { isNew, user, roles, filteringRuleGroups, timezones, loading } = useData(match);
  const [
    { title, tabs, breadcrumbs, selectedTab },
    { handleTabSelect },
  ] = usePage({ user, isNew });
  const [redirectUrl, handleFormSubmit, showSaveConfirmPopup, setShowSaveConfirmPopup, callSave, paramValues] = useFormSubmit(isNew);
  const handlePasswordReset = usePasswordReset(user?.email);

  if (redirectUrl) return <Redirect to={redirectUrl} />;

  return (
    <Page>
      {selectedTab.id === 2 && (
        <SignInLogPage
          onClose={() => handleTabSelect({ id: 1 })}
          userId={+user.id}
        />
      )}
      {selectedTab.id === 3 && (
        <ActivitiesPage
          entityId={+user.id}
          entityType="User"
          onClose={() => handleTabSelect({ id: 1 })}
        />
      )}

      <PageMenu>
        <Breadcrumbs links={breadcrumbs} />

        {!isNew && (
          <Fragment>
            <PageAction auto>
              <Tabs
                items={tabs}
                onChange={handleTabSelect}
                value={selectedTab}
              />
            </PageAction>

            <PageAction>
              <Button onClick={handlePasswordReset} orange sm solid>
                <Trans>PASSWORD_RESET</Trans>
              </Button>
            </PageAction>
          </Fragment>
        )}
      </PageMenu>

      <Box
        left={
          <PageContainer>
            <PageTitle>{title}</PageTitle>
            <PageContent spaced>
              {loading && <Loader left />}
              {!loading && (
                <UserForm
                  filteringRuleGroups={filteringRuleGroups}
                  isDeleted={!!user.deletedAt}
                  onSubmit={handleFormSubmit}
                  roles={roles}
                  timezones={timezones}
                  user={user}
                />
              )}
            </PageContent>
          </PageContainer>
        }
      />
      {showSaveConfirmPopup && <s.SaveConfirmationModalWrapper>
          <Popup          
          onClose={() => setShowSaveConfirmPopup(false) }
          customTitle={<s.SaveModalTitle className="modal-title">{t('CONFIRMATION_TITLE')}</s.SaveModalTitle>}
          withOverflow
          isCloseHidden
          className="small-container"
          >
          <div className="saq-modal-body">
            <p>{t('SAVE_USER_CONFIRMATION_MESSAGE')}</p>
          </div>
          <s.SaveModalButtonsWrapper>
            <EiqButton 
              variant='btn--outline-link'
              label={t('NO')}
              onClick={() => setShowSaveConfirmPopup(false)}
            />
            <EiqButton
              label={t('YES')}
              onClick={async (e) => {
                e.preventDefault();
                callSave(paramValues);
              }}
            />
          </s.SaveModalButtonsWrapper>
          </Popup>
        </s.SaveConfirmationModalWrapper>}

    </Page>
  );
};

UserPage.propTypes = {
  match: PropTypes.shape({}).isRequired,
};

export default React.memo(UserPage);
